import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { TrueImpactFloatMessage } from 'shared/design-system/components/TrueImpactFloatLoading';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ProgramProvider, {
  AnswerInput,
  useProgram,
} from 'shared/providers/ProgramProvider';
import { isAnswerFilled } from 'shared/providers/ProgramProvider/utils';
import { ResizableSplitView } from 'npo/pages/BetaFeedback/ResizableSplitView';
import ProgramSidebar from './ProgramSidebar';
import ProgramQuestionPane, { inputFromAnswer } from './ProgramQuestionPane';
import ReviewScreen from './ReviewScreen';
import SidebarIdentity from './SidebarIdentity';
import { ProgramProfileForm } from './ProgramProfileEditor';
import { faClipboardList } from '@fortawesome/pro-light-svg-icons';

// Float the close/expand controls over the top-right of the content so
// the sidebar runs edge-to-edge to the top of the modal.
const addStyles = `
  .modal-wrapper { position: relative; }
  .modal-wrapper .modal-header { position: absolute; top: 0; right: 0; z-index: 20; }
  .modal-wrapper .close { padding: ${SPACERS.tiny} ${SPACERS.small}; }
`;

const Body = styled.div`
  display: flex;
  height: 100%;
  min-height: 60vh;
`;

const RightPaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const RightPaneContent = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const RightPaneFooter = styled.div`
  padding: ${SPACERS.small} ${SPACERS.large};
  background-color: ${COLORS.whiteBase};
  border-top: 1px solid ${COLORS.grayLight};
  .error-text {
    text-align: center;
    margin-bottom: ${SPACERS.tiny};
  }
  /* Previous anchors left, the primary action anchors right, both aligned
     to the same 900px column the question content uses. */
  .buttons-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    .next-button {
      button {
        min-width: 200px;
        padding: ${SPACERS.small} ${SPACERS.large};
        font-size: 1rem;
      }
    }
  }
`;

interface ProgramQuestionsModalProps {
  open: boolean;
  programId: number | null;
  questionId?: number;
  programName?: string;
  onClose: () => void;
}

type Screen = 'question' | 'review';

const ProgramQuestionsBody: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const {
    program,
    questions,
    isAllComplete,
    submitProgram,
    submitting,
    activeQuestionId,
    setActiveQuestionId,
    findQuestionById,
    saveAnswer,
    saving,
  } = useProgram();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>('question');
  const [input, setInput] = useState<AnswerInput>(() =>
    inputFromAnswer(findQuestionById(activeQuestionId)?.question.answer),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!localError) return;
    const timer = window.setTimeout(() => setLocalError(null), 4000);
    return () => window.clearTimeout(timer);
  }, [localError]);

  const [hideFooter, setHideFooter] = useState(false);
  const [nextBlocked, setNextBlocked] = useState(false);

  const foundAnswer = findQuestionById(activeQuestionId)?.question.answer;

  // Sync `input` synchronously when the active question changes so child
  // components (QuestionRenderer / BeneficiaryField, which mirror props into
  // their own useState) mount with fresh values on the same render — not one
  // render late via a useEffect. See the React docs pattern for "storing
  // information from previous renders".
  const lastQuestionIdRef = useRef(activeQuestionId);
  if (lastQuestionIdRef.current !== activeQuestionId) {
    lastQuestionIdRef.current = activeQuestionId;
    setInput(inputFromAnswer(foundAnswer));
    setLocalError(null);
  }

  // Re-sync when the cached answer for the current question changes (e.g.
  // after a save patches the questions cache). This can stay as an effect
  // because the same question stays mounted; no child remount races.
  useEffect(() => {
    setInput(inputFromAnswer(foundAnswer));
  }, [foundAnswer]);

  // If the user clicks a question in the sidebar while reviewing, jump
  // straight back to editing that question.
  useEffect(() => {
    if (screen === 'review' && activeQuestionId != null) {
      setScreen('question');
    }
    // We intentionally only react to activeQuestionId changes.
  }, [activeQuestionId]);

  const isSubmitting = submitting.type === 'REQUEST_START';
  const isSaving = saving.type === 'REQUEST_START';
  const submitText = isAllComplete ? 'Submit changes' : 'Submit changes';

  const errorText = useMemo(() => {
    if (submitError) return submitError;
    if (program.type === 'REQUEST_ERROR') return program.error?.message;
    if (questions.type === 'REQUEST_ERROR') return questions.error?.message;
    return undefined;
  }, [submitError, program, questions]);

  const flatQuestionIds = useMemo(() => {
    if (questions.type !== 'REQUEST_SUCCESS') return [] as number[];
    return questions.data.sections.flatMap(s =>
      s.questions.map(q => q.questionId),
    );
  }, [questions]);

  const currentIndex = useMemo(
    () =>
      activeQuestionId == null ? -1 : flatQuestionIds.indexOf(activeQuestionId),
    [flatQuestionIds, activeQuestionId],
  );

  const prevQuestionId =
    currentIndex > 0 ? flatQuestionIds[currentIndex - 1] : null;
  const nextQuestionId =
    currentIndex >= 0 && currentIndex < flatQuestionIds.length - 1
      ? flatQuestionIds[currentIndex + 1]
      : null;
  const lastQuestionId =
    flatQuestionIds.length > 0
      ? flatQuestionIds[flatQuestionIds.length - 1]
      : null;

  const handleSubmit = async () => {
    setSubmitError(null);
    const res = await submitProgram();
    if (res.success) {
      onClose();
      return;
    }
    setSubmitError(res.error);
  };

  const handleSaveAndAdvance = async () => {
    setLocalError(null);
    const found = findQuestionById(activeQuestionId);
    if (!found) return;
    const { question } = found;

    const isEmpty = !isAnswerFilled(input);
    const hasExistingAnswer = Boolean(question.answer);

    // Allow skipping empty optional questions without persisting a blank row.
    if (isEmpty && !hasExistingAnswer && !question.required) {
      if (nextQuestionId != null) {
        setActiveQuestionId(nextQuestionId);
      } else {
        setScreen('review');
      }
      return;
    }

    const res = await saveAnswer(question, input);
    if (!res.success) {
      setLocalError(res.error);
      return;
    }
    if (nextQuestionId != null) {
      setActiveQuestionId(nextQuestionId);
    } else {
      setScreen('review');
    }
  };

  const handlePrevious = () => {
    if (screen === 'review') {
      setScreen('question');
      if (activeQuestionId == null && lastQuestionId != null) {
        setActiveQuestionId(lastQuestionId);
      }
      return;
    }
    if (prevQuestionId != null) {
      setActiveQuestionId(prevQuestionId);
    }
  };

  const handleEditFromReview = (questionId: number) => {
    setActiveQuestionId(questionId);
    setScreen('question');
  };

  // const title =
  //   program.type === 'REQUEST_SUCCESS'
  //     ? `${program.data.name} — Program Questions`
  //     : 'Program Questions';

  const previousDisabled =
    isSubmitting ||
    isSaving ||
    (screen === 'review'
      ? flatQuestionIds.length === 0
      : prevQuestionId == null);

  const nextIsSubmit = screen === 'review';
  const nextText = nextIsSubmit
    ? submitText
    : nextQuestionId != null
    ? 'Save & Continue'
    : 'Save & Review';
  const nextDisabled = nextIsSubmit
    ? isSubmitting
    : isSaving || activeQuestionId == null || nextBlocked;
  const nextIsLoading = nextIsSubmit ? isSubmitting : isSaving;
  const nextOnClick = nextIsSubmit ? handleSubmit : handleSaveAndAdvance;

  return (
    <TrueImpactModal
      open={true}
      // title={title}
      onClose={onClose}
      showExpandButton={true}
      initialExpandValue={true}
      maxWidth="1200px"
      hideFooter={true}
      addStyles={addStyles}
    >
      <Body>
        <ResizableSplitView
          defaultWidth={25}
          minWidth={15}
          maxWidth={50}
          left={
            <ProgramSidebar
              theme="program"
              backgroundColor="ctaBrandBackgroundLightest"
              onOpenReview={() => setScreen('review')}
              isReviewActive={screen === 'review'}
              onSelectQuestion={handleEditFromReview}
              identity={
                <SidebarIdentity
                  theme="program"
                  kickerIcon={faClipboardList}
                  title={
                    program.type === 'REQUEST_SUCCESS'
                      ? program.data.name
                      : 'Program Questions'
                  }
                  subtitle="Shared answers used across every report for this program."
                  progress={
                    questions.type === 'REQUEST_SUCCESS'
                      ? {
                          answered: questions.data.answeredCount,
                          total: questions.data.totalQuestions,
                        }
                      : undefined
                  }
                  badge={
                    program.type === 'REQUEST_SUCCESS' &&
                    program.data.isCompleted
                      ? { tone: 'success', label: 'Completed' }
                      : undefined
                  }
                  editorSlot={close => (
                    <ProgramProfileForm onCancel={close} onSaved={close} />
                  )}
                />
              }
            />
          }
          right={
            <RightPaneWrapper>
              <RightPaneContent>
                {screen === 'question' ? (
                  <ProgramQuestionPane
                    input={input}
                    onInputChange={setInput}
                    localError={localError}
                    disabled={isSaving}
                    onHideFooterChange={setHideFooter}
                    onBlockedChange={setNextBlocked}
                  />
                ) : (
                  <ReviewScreen onEditQuestion={handleEditFromReview} />
                )}
              </RightPaneContent>
              {!(hideFooter && screen === 'question') && (
                <RightPaneFooter>
                  {errorText && (
                    <TrueImpactText color="critical" className="error-text">
                      {errorText}
                    </TrueImpactText>
                  )}
                  <div className="buttons-wrapper">
                    <TrueImpactButtonWithLoading
                      variant="secondary-large"
                      disabled={previousDisabled}
                      isLoading={false}
                      onClick={handlePrevious}
                      className="previous-button"
                    >
                      Previous
                    </TrueImpactButtonWithLoading>
                    <TrueImpactButtonWithLoading
                      disabled={nextDisabled}
                      isLoading={nextIsLoading}
                      onClick={nextOnClick}
                      className="next-button"
                    >
                      {nextText}
                    </TrueImpactButtonWithLoading>
                  </div>
                </RightPaneFooter>
              )}
            </RightPaneWrapper>
          }
        />
      </Body>
      <TrueImpactFloatMessage type="error" message={localError} />
    </TrueImpactModal>
  );
};

// Gate that defers mounting ProgramQuestionsBody until program + questions
// (which carry inline answers) have loaded. Without this, the body's
// `useState` initializers seed from an unresolved provider, and downstream
// fields that mirror props into their own useState (e.g. BeneficiaryField)
// capture the empty snapshot and never rehydrate.
const ProgramQuestionsGate: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { program, questions, activeQuestionId } = useProgram();
  const ready =
    program.type === 'REQUEST_SUCCESS' &&
    questions.type === 'REQUEST_SUCCESS' &&
    activeQuestionId != null;

  // Only treat as "empty" when the loaded questions result truly has no
  // questions. Without the sections check we hit a render/effect race: on the
  // tick where `questions` first flips to REQUEST_SUCCESS, `activeQuestionId`
  // is still null because the ProgramProvider effect that seeds it (child
  // effects run before parent effects) hasn't fired yet — which caused the
  // modal to open and immediately close itself.
  const loadedButEmpty =
    program.type === 'REQUEST_SUCCESS' &&
    questions.type === 'REQUEST_SUCCESS' &&
    activeQuestionId == null &&
    !questions.data?.sections?.some(s => s.questions.length > 0);

  useEffect(() => {
    if (loadedButEmpty) onClose();
  }, [loadedButEmpty, onClose]);

  if (!ready) {
    return (
      <TrueImpactModal
        open={true}
        onClose={onClose}
        showExpandButton={true}
        initialExpandValue={true}
        maxWidth="1200px"
        hideFooter={true}
        addStyles={addStyles}
      >
        <TrueImpactLoading />
      </TrueImpactModal>
    );
  }
  return <ProgramQuestionsBody onClose={onClose} />;
};

const ProgramQuestionsModal: React.FC<ProgramQuestionsModalProps> = ({
  open,
  programId,
  questionId,
  programName = 'your program',
  onClose,
}) => {
  const outer = useProgram();
  // Seed the outer provider's activeQuestionId when opening from a specific
  // question, so it matches the initialQuestionId behaviour of the inner
  // provider we skip below.
  useEffect(() => {
    if (open && questionId != null && outer.programId === programId) {
      outer.setActiveQuestionId(questionId);
    }
  }, [open, questionId, programId, outer.programId]);

  if (!open || programId == null) return null;

  // If an outer ProgramProvider already covers this programId (e.g. from the
  // Report Builder), reuse it so answer saves propagate to consumers outside
  // the modal. Otherwise, spin up a scoped provider for the modal.
  if (outer.programId === programId) {
    return <ProgramQuestionsGate onClose={onClose} />;
  }
  return (
    <ProgramProvider
      programId={programId}
      initialQuestionId={questionId}
      programName={outer.programName || programName}
    >
      <ProgramQuestionsGate onClose={onClose} />
    </ProgramProvider>
  );
};

export default ProgramQuestionsModal;
